import React from 'react';
import type { GoodsOption } from 'shared/src/api';
import { Colors, Flex, IconClose, InputBox, Typography } from '@banhala/ui-kit';
import { isNil, isNotNil, localizeNumber, isWeb } from 'shared/src/utils';
import { CounterStepper } from './CounterStepper';
import { useGoodsOptionsBrief } from '../../queries/useGoodsOptionsBrief';
import { useScrollIntoViewOnFocus } from 'shared/src/hooks';
import { PricingBadge } from './PricingBadge';
const MAX_TEXT_OPTION_LENGTH = 255;

/**
 * 텍스트 옵션 입력창의 글자 크기.
 *
 * 웹만 `body1`(16px)을 쓴다. iOS Safari 는 **16px 미만** 인풋에 포커스하면 글자가 16px 로
 * 보이도록 페이지를 확대하고(`user-scalable=no` 는 iOS 10 부터 무시된다) 포커스 위치로
 * 스크롤한다. 그러면
 * - 보이는 영역(`visualViewport.height`)이 확대 배율만큼 줄어들고
 * - RNW `Dimensions` 는 높이를 `visualViewport.height * scale` 로 되돌려 보고하므로
 *   (react-native-web `Dimensions/index.js`) 시트가 잡는 높이와 실제 보이는 높이가 어긋나고
 * - `position: fixed` 오버레이인 바텀시트가 밀린 좌표계와 맞지 않아 위쪽이 잘린다.
 *
 * 실측: body2(14px)일 때 `Dimensions.height / visualViewport.height = 1.1418 ≈ 16/14`,
 * `visualViewport.offsetTop = 192`.
 */
const TEXT_OPTION_INPUT_VARIANT = isWeb ? 'body1' : 'body2';

export const PricingListItem: React.FC<{
  option: GoodsOption;
  onUpdateOption: (option: GoodsOption) => void;
  onPressDelete: () => void;
}> = props => {
  const { option } = props;
  const { data: goodsOptionBrief } = useGoodsOptionsBrief();
  // 좁아진 시트 안에서 포커스한 입력창이 접힌 아래에 남지 않게 리스트를 스크롤한다(웹 전용).
  const { ref: messageInputRef, onFocus: onFocusMessageInput } =
    useScrollIntoViewOnFocus();

  return (
    <Flex
      padding={{ horizontal: 16, vertical: 16 }}
      borderRadius={8}
      borderWidth={1}
      borderColor={'border_secondary'}
      gap={12}
    >
      <Flex gap={8}>
        <Flex.Row alignItems={'center'} justifyContent={'space-between'}>
          <Flex flex={1}>
            <Typography type={'body3'} numberOfLines={3}>
              {option.option_names.join(' / ')}
            </Typography>
          </Flex>
          <Flex margin={{ left: 12 }} onPress={props.onPressDelete}>
            <IconClose size={18} fill={Colors.content_disabled} />
          </Flex>
        </Flex.Row>
        {isNotNil(goodsOptionBrief) &&
          isNotNil(goodsOptionBrief.text_option) && (
            <Flex gap={8}>
              <Typography type={'subtitle4'} color={'content_primary'}>
                {goodsOptionBrief.text_option.title}
              </Typography>
              <InputBox
                ref={messageInputRef}
                onFocus={onFocusMessageInput}
                type={'outline'}
                variant={TEXT_OPTION_INPUT_VARIANT}
                maxLength={MAX_TEXT_OPTION_LENGTH}
                placeholder={goodsOptionBrief.text_option.placeholder}
                value={option.message ?? ''}
                onChangeText={text => {
                  props.onUpdateOption({
                    ...option,
                    message: text === '' ? undefined : text,
                  });
                }}
              ></InputBox>
            </Flex>
          )}
        <PricingBadge
          deliveryType={option.delivery_type}
          salesMethod={goodsOptionBrief?.sales_method}
        />
      </Flex>

      <Flex.Row
        height={40}
        justifyContent={'space-between'}
        alignItems={'center'}
      >
        <Flex>
          <CounterStepper
            max={
              isNil(props.option.max_order_limit_count)
                ? props.option.stock
                : Math.min(
                    props.option.stock,
                    props.option.max_order_limit_count,
                  )
            }
            min={1}
            value={props.option.purchase_count ?? 1}
            onChangeValue={nextVal => {
              props.onUpdateOption({
                ...props.option,
                purchase_count: nextVal,
              });
            }}
          />
        </Flex>
        <Flex alignItems={'flex-end'} gap={4}>
          <Typography type={'subtitle1'}>
            {localizeNumber(
              props.option.price * (props.option.purchase_count ?? 1),
            )}
            원
          </Typography>
          <Flex.Row gap={4}>
            {props.option.stock >= 1 && props.option.stock <= 9 && (
              <Typography type={'body4'} color={'content_pink_primary'}>
                {props.option.stock}개 남음
              </Typography>
            )}
            {isNotNil(props.option.max_order_limit_count) && (
              <Typography type={'body4'} color={'content_tertiary'}>
                (인당 최대 {localizeNumber(props.option.max_order_limit_count)}
                개 구매가능)
              </Typography>
            )}
          </Flex.Row>
        </Flex>
      </Flex.Row>
    </Flex>
  );
};
